<script setup>
import { computed } from 'vue';
import FloatingSelect from '@/components/FloatingSelect.vue';

const emit = defineEmits(['update:modelValue', 'select']);
const props = defineProps({
  modelValue: { type: [String, Number, null], default: null },
  options: { type: Array, default: () => [] },
  keyProp: { type: String, default: 'id' },
  labelKey: { type: [String, Function], default: 'name' },
  placeholder: { type: String, default: 'Auswahl treffen' },
  ariaLabel: { type: String, default: 'Auswahl' },
  disabled: { type: Boolean, default: false },
  searchProp: { type: [String, Array, Function], default: 'name' },
  groupedSearch: { type: Boolean, default: false},
  size: { type: String, default: 'sm' },
  matchTriggerWidth: { type: Boolean, default: true },
});

const normalizedOptions = computed(() => Array.isArray(props.options)
  ? props.options.map(o => ({
      ...o,
      [props.keyProp]: o?.[props.keyProp],
    })).filter(o => o[props.keyProp] !== undefined)
  : []
);

const selected = computed(() => {
  const v = props.modelValue;
  if (v === null || v === undefined) return null;
  return normalizedOptions.value.find(o => String(o?.[props.keyProp]) === String(v)) || null;
});

const selectedText = computed(() => {
  const opt = selected.value;
  if (!opt) return '';
  if (typeof props.labelKey === 'function') return String(props.labelKey(opt));
  return String(opt?.[props.labelKey] ?? '');
});

function onUpdate(val) {
  emit('update:modelValue', val);
  const opt = normalizedOptions.value.find(o => String(o?.[props.keyProp]) === String(val));
  emit('select', opt ?? null);
}
</script>

<template>
  <FloatingSelect
    :model-value="modelValue"
    :options="normalizedOptions"
    :option-key="keyProp"
    :option-label="labelKey"
    :search-prop="searchProp"
    :grouped-search="groupedSearch"
    :size="size"
    :disabled="disabled"
    :match-trigger-width="matchTriggerWidth"
    :return-object="false"
    :aria-label="ariaLabel"
    placeholder=""
    @update:modelValue="onUpdate"
  >
    <template #trigger="{ open }">
      <div class="fd-trigger-wrap">
        <button type="button" class="fd-trigger" :class="['size-' + size, { 'is-open': open, 'is-disabled': disabled }]" :aria-expanded="open.toString()" :aria-label="ariaLabel">
          <div class="fd-trigger-content">
            <span class="fd-text" :class="{ 'is-placeholder': !selectedText }">{{ selectedText || placeholder }}</span>
            <button v-if="selected" type="button" class="fd-clear" @click.stop="onUpdate(props.modelValue === null || props.modelValue === undefined ? null : (typeof props.modelValue === 'number' ? -1 : ''))" :aria-label="'Auswahl entfernen'" :disabled="disabled">
              <!-- <span aria-hidden="true">×</span> -->
            </button> 
          </div>
          <svg class="fd-chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
        </button>
      </div>
    </template>
    <template #item="{ option, highlighted }">
      <div v-if="option.__header" class="fd-header" :class="option.__class">
        <span class="fd-header-text">{{ option.name || option.label }}</span>
      </div>
      <div v-else class="fd-item" :class="{ 'is-highlighted': highlighted, 'is-child': !!option.__child, 'is-parent': !!option.__parent }">
        <span class="fd-label">{{ typeof labelKey === 'function' ? labelKey(option) : option?.[labelKey] }}</span>
      </div>
    </template>
  </FloatingSelect>
</template>

<style scoped>
.fd-trigger-wrap { display: flex; align-items: center; position: relative; }
.fd-trigger-content { display: flex; align-items: center; gap: .5rem; z-index: 2;}
.fd-clear { position: relative; border: none; background: none; padding: 0; width: 12px; height: 12px;}
.fd-clear::before { content: url('@/assets/imgs/icons/circle-xmark-solid.svg'); transform: translateY(-50%); width: 12px; height: 12px; filter: invert(15%) sepia(94%) saturate(3495%) hue-rotate(350deg) brightness(85%) contrast(101%)}
.fd-clear:hover { background: #d5e2e8; }
.fd-trigger { display:flex; align-items:center; justify-content:space-between; gap:.5rem; width:100%; padding:.5rem .65rem; border:1px solid #c3ced6; border-radius:.65rem; background:#fff; color:#13242c; transition:.18s border-color,.18s box-shadow,.18s background; }
.fd-trigger:hover { background:#f5fafc; }
.fd-trigger.is-disabled { opacity:.5; cursor:not-allowed; }
.fd-trigger:focus { outline:none; border-color:var(--sellwerk-color-main-accent,#008fb3); box-shadow:0 0 0 3px rgba(var(--sellwerk-color-main-accent-rgb,0,143,179), .25); }
.fd-text { font-size:.75rem; line-height:1.1; }
.fd-text.is-placeholder { opacity:.6; }
.fd-chevron { opacity:.7; }
.size-sm.fd-trigger { padding:.42rem .55rem; border-radius:.6rem; }
.size-lg.fd-trigger { padding:.65rem .85rem; }
.fd-item { display:flex; align-items:center; gap:.5rem; font-size:.66rem; }
.fd-header { display:flex; align-items:center; gap:.5rem; font-weight:700; font-size:.6rem; letter-spacing:.5px; text-transform:uppercase; opacity:.95; padding:.25rem .35rem; padding-left:0; margin:.1rem 0 .1rem 0; border:none; border-radius:.25rem; }
.fd-header.hdr-tour { background: rgba(0, 143, 179, .12); color:#0d3b46; }
.fd-header.hdr-tasks { background: rgba(245, 158, 11, .18); color:#4a2b00; }
.fd-header.hdr-google { background: rgba(34, 197, 94, .16); color:#0f3a1f; }
.fd-header.hdr-city { background: rgba(59, 130, 246, .14); color:#0b2a68; }
.fd-header-text { padding:0; }
.fd-item.is-child { padding-left: 0; }
</style>
